<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">Delivery Reports</h3>
                <div class="row breadcrumbs-top">
                    <div class="breadcrumb-wrapper col-12">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/">Home</a></li>
                            <li class="breadcrumb-item active"><a href="/admin/delivery-reports">Delivery Reports</a>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-header">
                    <h4>Delivery Analytics</h4>
                </div>
                <form @submit.prevent="fetchDeliveryAnalytics">
                    <div class="card-body">
                        <div class="row">
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Start date</label>
                                <date-time-field v-model="analyticsFilter.start_date" class="form-control" bound="start"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>End date</label>
                                <date-time-field v-model="analyticsFilter.end_date" class="form-control" bound="end"></date-time-field>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>State</label>
                                <select v-model="analyticsFilter.state_id" class="form-control" @change="onAnalyticsStateChange">
                                    <option value="">All states</option>
                                    <option v-for="state in states" :key="`analytics-state-${state.id}`" :value="state.id">
                                        {{ state.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6">
                                <label>Store</label>
                                <select v-model="analyticsFilter.store_id" class="form-control">
                                    <option value="">All stores</option>
                                    <option v-for="store in analyticsStores" :key="`analytics-store-${store.id}`" :value="store.id">
                                        {{ store.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Order Type</label>
                                <select v-model="analyticsFilter.order_type" class="form-control">
                                    <option v-for="type in orderTypes" :key="type" :value="type">{{ type }}</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Order Channel</label>
                                <select v-model="analyticsFilter.ordered_from" class="form-control">
                                    <option value="">All channels</option>
                                    <option v-for="(value, name) in platforms" :key="value" :value="value">
                                        {{ name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Payment Status</label>
                                <select v-model="analyticsFilter.payment_status" class="form-control">
                                    <option value="">All</option>
                                    <option v-for="status in paymentStatuses" :key="status" :value="status">
                                        {{ status }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Payment Channel</label>
                                <select v-model="analyticsFilter.payment_channel" class="form-control">
                                    <option value="">All channels</option>
                                    <option v-for="channel in paymentChannels" :key="channel" :value="channel">
                                        {{ channel }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Product</label>
                                <select v-model="analyticsFilter.product_id" class="form-control">
                                    <option value="">All products</option>
                                    <option v-for="product in products" :key="`analytics-product-${product.id}`" :value="product.id">
                                        {{ product.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Customer Type</label>
                                <select v-model="analyticsFilter.customer_type" class="form-control">
                                    <option value="">All customers</option>
                                    <option value="new">New</option>
                                    <option value="returning">Returning</option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Delivery Status</label>
                                <select v-model="analyticsFilter.status" class="form-control">
                                    <option value="">All statuses</option>
                                    <option v-for="status in orderStatuses" :key="status" :value="status">
                                        {{ status }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>Rider</label>
                                <select v-model="analyticsFilter.rider_id" class="form-control">
                                    <option value="">All riders</option>
                                    <option v-for="rider in riders" :key="`analytics-rider-${rider.id}`" :value="rider.id">
                                        {{ rider.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-xl-3 col-md-4 col-sm-6 mt-1">
                                <label>&nbsp;</label><br>
                                <button type="submit" class="btn btn-primary" :disabled="isLoadingAnalytics">Filter</button>
                                <button type="button" class="btn btn-default" @click="resetDeliveryAnalytics">Reset</button>
                            </div>
                        </div>
                    </div>
                </form>
            </section>

            <section class="card">
                <div class="card-body">
                    <p class="text-center" v-if="analytics.date_label">
                        <b>{{ analytics.date_label }}</b>
                    </p>
                    <div class="row">
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.number_of_deliveries }}</h3>
                            <p class="sub-heading mb-0">Number of Deliveries</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.completed_deliveries }}</h3>
                            <p class="sub-heading mb-0">Completed Deliveries</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.pending_deliveries }}</h3>
                            <p class="sub-heading mb-0">Pending Deliveries</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.failed_deliveries }}</h3>
                            <p class="sub-heading mb-0">Failed Deliveries</p>
                        </div>
                        <div class="col-lg-4 col-md-6 mb-2">
                            <h3 class="heading-text text-bold-600">{{ analytics.average_delivery_time }}</h3>
                            <p class="sub-heading mb-0">Average Delivery Time</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card">
                <div class="card-header">
                    <h4>Best Performing Stores by Delivery Performance</h4>
                </div>
                <div class="card-body">
                    <BarChart v-if="storePerformanceBar" :chart-data="storePerformanceBar" :chart-options="{responsive: true}"/>
                    <p class="text-center mb-0" v-else>No completed delivery times for the selected filters.</p>
                </div>
            </section>

            <div class="row">
                <div class="col-12">
                    <download-csv class="btn btn-warning btn-sm mb-2 print-btn" :data="json_data"
                                  name="delivery_report.csv">
                        <i class="fas fa-file-csv"></i> Export to CSV
                    </download-csv>
                </div>
            </div>

            <section class="card">

                <form @submit.prevent="filterData">
                    <div class="d-flex justify-content-center mt-2 mb-2">
                        <div class="col-xl-4 col-md-4">
                            <label>Start date</label><br/>
                            <date-time-field v-model="reportFilter.start_date" class="form-control" required bound="start"></date-time-field>
                        </div>

                        <div class="col-xl-4 col-md-4">
                            <label>End date</label><br/>
                            <date-time-field v-model="reportFilter.end_date" class="form-control" required bound="end"></date-time-field>
                        </div>

                        <div class="col-xl-4 col-md-4">
                            <label>&nbsp;</label><br/>
                            <button type="submit" class="btn btn-primary">Filter</button>
                            <button type="button" class="btn btn-default" @click="resetOverallFilter">Reset</button>
                        </div>
                    </div>
                </form>

                <div class="card-body">
                    <div class="row">
                        <div class="col-12 p-3">
                            <PieChart v-if="loaded" :chart-data="pieData" :options="pieOptions"/>
                        </div>
                    </div>
                </div>

                <p class="text-center" v-if="!isFiltered">
                    <!-- <b>June 2021 to {{ getCurrentMonthName() }}</b> -->
                    <b> {{ getCurrentMonthName() }}</b>

                </p>

                <p class="text-center" v-if="isFiltered">
                    <b>
                        {{ dateFormatted(reportFilter) }}
                    </b>
                </p>

            </section>

            <section class="card">
                <div class="card-header">
                    <h4>Total delivery and pickup orders</h4>
                </div>

                <div class="card-body">

                    <download-csv class="btn btn-warning btn-sm mb-2 print-btn" :data="json_data"
                                  name="delivery_report.csv">
                        <i class="fas fa-file-csv"></i> Export to CSV
                    </download-csv>

                    <form @submit.prevent="fetchPickupOrDeliveryCountReportByStore">
                        <div class="row">

                            <div class="col-6 col-md-6">
                                Select a state (optional)
                                <select v-model="stateId" class="form-control">
                                    <option value="">Select a state</option>
                                    <option v-for="state in states" :key="state.id"
                                            :value="state.id">
                                        {{ state.name }}
                                    </option>
                                </select>
                            </div>


                            <div class="col-6 col-md-6">
                                Select a store (Optional)
                                <select v-model="storeId" class="form-control">
                                    <option value="">Select a store</option>
                                    <option v-for="store in stores" :key="store.id"
                                            :value="store.id">
                                        {{ store.name }}
                                    </option>
                                </select>
                            </div>

                            <div class="col-6 col-md-6" style="margin-top: 1em;">
                                <label>Start date</label><br/>
                                <date-time-field v-model="deliveryCount.startDate" class="form-control" bound="start"></date-time-field>
                            </div>

                            <div class="col-6 col-md-6" style="margin-top: 1em;">
                                <label>End date</label><br/>
                                <date-time-field v-model="deliveryCount.endDate" class="form-control" bound="end"></date-time-field>
                            </div>

                            <div class="col-md-4">
                                <div class="form-group">

                                    <label>&nbsp;</label><br/>
                                    <button type="submit" class="btn btn-primary">
                                        Filter
                                    </button>
                                    <button type="button" class="btn btn-default"
                                            @click="resetTotalDeliveryPickupFilter">
                                        Reset Filter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>

                    <br><br>
                    <div class="row">
                        <div class="col-lg-6 col-md-6 col-xl-6 col-sm-6 col-12">
                            <div
                                class="d-flex align-items-start mb-sm-1 mb-xl-0 border-right-blue-grey border-right-lighten-5">
                                                <span class="card-icon warning d-flex justify-content-center mr-3">
                                                    <i class="icon p-1 icon-basket-loaded customize-icon font-large-2 p-1"></i>
                                                </span>
                                <div class="stats-amount mr-3">
                                    <h3 class="heading-text text-bold-600">
                                        {{ deliveryCount.pickup }}
                                    </h3>
                                    <p class="sub-heading">
                                        Total number of pickup orders
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-6 col-md-6 col-xl-6 col-sm-6 col-12">
                            <div class="d-flex align-items-start">
                                                <span class="card-icon primary d-flex justify-content-center mr-3">
                                                    <i class="icon p-1 icon-bar-chart customize-icon font-large-2 p-1"></i>
                                                </span>
                                <div class="stats-amount mr-3">
                                    <h3 class="heading-text text-bold-600">
                                        {{ deliveryCount.delivery }}
                                    </h3>
                                    <p class="sub-heading">
                                        Total number of delivery orders
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="card">
                <div class="card-header">
                    <h4>Completed Dispatch Reports</h4>
                </div>

                <div class="card-body">
                    <form @submit.prevent="filterCompletedDispatchesReports">
                        <div class="row">
                            <div class="col-xl-4 col-md-4">
                                Select a rider
                                <select v-model="completedRiderId" class="form-control">
                                    <option value="">Select a rider</option>
                                    <option v-for="rider in riders" :key="rider.id"
                                            :value="rider.id">
                                        {{ rider.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-4">
                                <div class="form-group">
                                    <label for="start-date">Start Date</label>
                                    <date-time-field class="form-control form-control-sm" v-model="completeFilter.start_date" bound="start"></date-time-field>
                                </div>
                            </div>

                            <div class="col-4">
                                <div class="form-group">
                                    <label for="end-date">End Date</label>
                                    <date-time-field class="form-control form-control-sm" v-model="completeFilter.end_date" bound="end"></date-time-field>
                                </div>
                            </div>

                            <div class="col-md-4">
                                <div class="form-group">
                                    <button type="submit" class="btn btn-sm btn-primary"
                                            :disabled="isLoadingCompletedFilter">
                                        Apply Filter
                                        <i class="fa fa-spinner spinner" v-if="isLoadingCompletedFilter"></i>
                                    </button>
                                    <button type="reset" @click="resetOverallFilterComplete"
                                            class="btn btn-sm btn-warning">
                                        Reset Filter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>

                    <div class="row">
                        <div class="col-12 d-flex mb-2 justify-content-end">
                            <a href="#" v-if="isFetchingCompletedExportData !== 'csv'"
                               @click.prevent="startCompletedDispatchReportsDownload('csv')"
                               class="export-link mr-1"><i
                                class="fa fa-file-excel-o"></i>
                                Export to csv</a>
                            <i class="fa fa-spinner fa-spin mr-3" v-if="isFetchingCompletedExportData === 'excel'"></i>

                            <download-excel v-show="false" ref="completed-exporter-csv" :data="exportRows" :export-fields="exportCompletedFields" :name="generateReportName('Completed_Dispatch_Report', '.csv')" />
                            <download-excel v-show="false" ref="completed-exporter-xls" :data="exportRows" :export-fields="exportCompletedFields" :name="generateReportName('Completed_Dispatch_Report', '.xls')" />
                            <!-- <a href="#" v-if="isFetchingCompletedExportData !== 'csv'" class="export-link mr-1"
                               @click.prevent="startCompletedDispatchReportsDownload('csv')"><i
                                class="fas fa-file-csv"></i>
                                Export to csv </a>
                            <i class="fa fa-spinner fa-spin mr-3" v-if="isFetchingCompletedExportData === 'csv'"></i>
                            | &nbsp;
                            <download-excel v-if="dispatchCompletedExportData.length > 0" v-show="false" type="csv"
                                            :data="dispatchCompletedExportData"
                                            :export-fields="exportCompletedFields">
                                <a href="#" ref="exportCompletedCSVButton" class="export-link"><i
                                    class="fas fa-file-csv"></i>
                                    Export to csv</a>
                                | &nbsp;
                            </download-excel>         -->


                            <a href="#" v-if="isFetchingCompletedExportData !== 'excel'"
                               @click.prevent="startCompletedDispatchReportsDownload('excel')"
                               class="export-link text-warning"><i
                                class="fa fa-file-excel-o"></i>
                                Export to excel</a>
                            <i class="fa fa-spinner fa-spin ml-3" v-if="isFetchingCompletedExportData === 'excel'"></i>

                            <download-excel v-show="false" ref="completed-exporter-xls" :data="exportRows" :export-fields="exportCompletedFields" :name="generateReportName('Completed_Dispatch_Report', '.xls')" />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12">
                            <vue-good-table
                                class="dataTable"
                                mode="remote"
                                :isLoading.sync="isLoading"
                                :totalRows="totalRecords"
                                :pagination-options="paginationOptions"
                                @on-page-change="onPageChangeCompleted"
                                @on-per-page-change="onPerPageChangeCompleted"
                                @on-sort-change="onSortChangeCompleted"
                                styleClass="vgt-table"
                                :columns="completed_columns"
                                :rows="completed_dispatches"
                            >
                                <template slot="table-row" slot-scope="props">
                                    <span v-if="props.column.field === 'order_number'">
                                        <a :href="`/admin/orders/${props.row.order_id}`">
                                            {{ props.row.order_number }}
                                        </a>
                                    </span>
                                </template>
                            </vue-good-table>
                        </div>
                    </div>
                </div>

            </section>

            <section class="card">
                <div class="card-header">
                    <h4>Pending Dispatch Reports</h4>
                </div>

                <div class="card-body">
                    <form @submit.prevent="filterPendingDispatchesReports">
                        <div class="row">
                            <div class="col-xl-4 col-md-4">
                                Select a rider
                                <select v-model="pendingRiderId" class="form-control">
                                    <option value="">Select a rider</option>
                                    <option v-for="rider in riders" :key="rider.id"
                                            :value="rider.id">
                                        {{ rider.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-4">
                                <div class="form-group">
                                    <label for="start-date">Start Date</label>
                                    <date-time-field id="start-date" class="form-control form-control-sm" v-model="pendingFilter.start_date" bound="start"></date-time-field>
                                </div>
                            </div>

                            <div class="col-4">
                                <div class="form-group">
                                    <label for="end-date">End Date</label>
                                    <date-time-field id="end-date" class="form-control form-control-sm" v-model="pendingFilter.end_date" bound="end"></date-time-field>
                                </div>
                            </div>

                            <div class="col-md-4">
                                <div class="form-group">
                                    <button type="submit" class="btn btn-sm btn-primary"
                                            :disabled="isLoadingPendingFilter">
                                        Apply Filter
                                        <i class="fa fa-spinner spinner" v-if="isLoadingPendingFilter"></i>
                                    </button>
                                    <button type="reset" @click="resetOverallFilterPending"
                                            class="btn btn-sm btn-warning">
                                        Reset Filter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>

                    <div class="row">
                        <div class="col-12 d-flex mb-2 justify-content-end">
                            <a href="#" v-if="isFetchingPendingExportData !== 'csv'" class="export-link mr-1"
                               @click.prevent="startPendingDispatchReportsDownload('csv')"><i
                                class="fas fa-file-csv"></i>
                                Export to csv </a>
                            <i class="fa fa-spinner fa-spin mr-3" v-if="isFetchingPendingExportData === 'csv'"></i>
                            | &nbsp;
                            <download-excel v-show="false" ref="pending-exporter-csv" :data="exportRows" :export-fields="exportPendingFields" :name="generateReportName('Pending_Dispatch_Report', '.csv')" />

                            <a href="#" v-if="isFetchingPendingExportData !== 'excel'"
                               @click.prevent="startPendingDispatchReportsDownload('excel')"
                               class="export-link text-warning"><i
                                class="fa fa-file-excel-o"></i>
                                Export to excel</a>
                            <i class="fa fa-spinner fa-spin ml-3" v-if="isFetchingPendingExportData === 'excel'"></i>

                            <download-excel v-show="false" ref="pending-exporter-xls" :data="exportRows" :export-fields="exportPendingFields" :name="generateReportName('Pending_Dispatch_Report', '.xls')" />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12">
                            <vue-good-table
                                class="dataTable"
                                mode="remote"
                                :isLoading.sync="isLoading"
                                :totalRows="totalRecords"
                                :pagination-options="paginationOptions"
                                @on-page-change="onPageChangePending"
                                @on-per-page-change="onPerPageChangePending"
                                @on-sort-change="onSortChangePending"
                                styleClass="vgt-table"
                                :columns="pending_columns"
                                :rows="pending_dispatches"
                            >
                                <template slot="table-row" slot-scope="props">
                                    <span v-if="props.column.field === 'order_number'">
                                        <a :href="`/orders/${props.row.order_id}/details`">
                                            {{ props.row.order_number }}
                                        </a>
                                    </span>
                                </template>
                            </vue-good-table>
                        </div>
                    </div>
                </div>

            </section>
        </div>

    </div>
</template>

<script>
import axios from 'axios';
import {formatDate, handleErrors} from "../../../utils";
import PieChart from "../../../Shared/PieChart.vue";
import BarChart from "../../../Shared/BarChart.vue";
import Layout from '@/Shared/Layout';
import TableExportMixin from "../../../Shared/Mixins/TableExportMixin";

export default {
    metaInfo: {title: 'Delivery Reports'},
    layout: Layout,
    mixins: [TableExportMixin],
    components: {PieChart, BarChart},
    data: () => ({
        loaded: false,
        pieOptions: {
            responsive: true,
            maintainAspectRatio: false
        },
        isLoadingPendingFilter: false,
        isLoadingCompletedFilter: false,
        completeFilter: {
            start_date: '',
            end_date: ''
        },
        pendingFilter: {
            start_date: '',
            end_date: ''
        },
        pieData: [],
        reportFilter: {},
        json_data: [],
        completed_dispatches: [],
        pending_dispatches: [],
        completed_columns: [
            {label: 'Order number', field: 'order_number'},
            {label: 'Rider name', field: 'rider_name'},
            {label: 'Rider state', field: 'rider_state'},
            {label: 'Assigned time', field: 'assigned_time'},
            {label: 'Started time', field: 'started_time'},
            {label: 'Completed time', field: 'completed_time'},
            {label: 'Delivery period', field: 'delivery_period'},
            {label: 'Order date', field: 'order_date'}
        ],
        pending_columns: [
            {label: 'Order number', field: 'order_number'},
            {label: 'Rider name', field: 'rider_name'},
            {label: 'Rider state', field: 'rider_state'},
            {label: 'Assigned time', field: 'assigned_time'},
            {label: 'Order date', field: 'order_date'}
        ],
        sortOptions: {
            enabled: true,
            initialSortBy: {field: 'order_date', type: 'desc'}
        },
        paginationOptions: {
            enabled: true,
            mode: 'pages',
            perPage: 10,
            perPageDropdown: [5, 10, 20, 50, 100, 200]
        },
        isLoading: false,
        totalRecords: 0,
        serverParams: {
            columnFilters: {},
            sort: {
                field: '',
                type: ''
            },
            page: 1,
            perPage: 10
        },
        dispatchCompletedExportData: [],
        dispatchPendingExportData: [],
        exportRows: [],
        isFetchingCompletedExportData: null,
        isFetchingPendingExportData: null,
        label: "",
        isFiltered: false,
        storeId: '',
        stateId: '',
        completedRiderId: '',
        pendingRiderId: '',
        stores: [],
        analyticsStores: [],
        states: [],
        riders: [],
        products: [],
        platforms: {},
        orderTypes: ['DELIVERY', 'PICKUP'],
        paymentStatuses: [],
        paymentChannels: [],
        orderStatuses: [],
        isLoadingAnalytics: false,
        storePerformanceBar: null,
        analyticsFilter: {
            start_date: '',
            end_date: '',
            state_id: '',
            store_id: '',
            order_type: 'DELIVERY',
            ordered_from: '',
            payment_status: '',
            payment_channel: '',
            product_id: '',
            customer_type: '',
            status: '',
            rider_id: '',
        },
        analytics: {
            number_of_deliveries: 0,
            completed_deliveries: 0,
            pending_deliveries: 0,
            failed_deliveries: 0,
            average_delivery_time: '0m',
            date_label: '',
        },
        deliveryCount: {
            startDate: null,
            endDate: null,
            pickup: 0,
            delivery: 0,
        },
    }),
    mounted() {
        this.fetchData();
        this.fetchDeliveryAnalytics();
        this.fetchCompletedDispatchesReports();
        this.fetchPendingDispatchesReports();
        this.fetchPickupOrDeliveryCountReportByStore();
    },
    methods: {
        async startCompletedDispatchReportsDownload(type = 'csv') {
            const fileType = type === 'excel' ? 'xls' : type;
            await this.exportPostReportData('admin.reports.delivery.completed-dispatch-reports', {
                start_date: this.completeFilter.start_date,
                end_date: this.completeFilter.end_date,
                rider_id: this.completedRiderId,
            }, fileType, 'completed');
        },


        async fetchPickupOrDeliveryCountReportByStore() {
            try {
                const {
                    data: {
                        data: {
                            pickupDeliveryReport: pickupDeliveryReport,
                        }
                    }
                } = await axios.post(route("admin.reports.delivery.delivery-pickup-counts"), {
                    'store_id': this.storeId ?? null,
                    'state_id': this.stateId ?? null,
                    'start_date': this.deliveryCount.startDate,
                    'end_date': this.deliveryCount.endDate,
                });

                console.log(pickupDeliveryReport);
                this.json_data = [pickupDeliveryReport];
                this.deliveryCount.pickup = pickupDeliveryReport.pickup;

                this.deliveryCount.delivery = pickupDeliveryReport.delivery;

                this.loaded = true;
            } catch (e) {
                console.log(e.response);
            }
        },

        async startPendingDispatchReportsDownload(type = 'csv') {
            const fileType = type === 'excel' ? 'xls' : type;
            await this.exportPostReportData('admin.reports.delivery.pending-dispatch-reports', {
                start_date: this.pendingFilter.start_date,
                end_date: this.pendingFilter.end_date,
                rider_id: this.pendingRiderId,
            }, fileType, 'pending');
        },

        pieThumbprint(labels, data, backgroundColor) {
            return {
                labels,
                datasets: [
                    {
                        backgroundColor,
                        data
                    }
                ]
            }
        },

        updateParamsCompleted(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChangeCompleted(params) {
            console.log('change completed')
            this.updateParamsCompleted({page: params.currentPage});
            if (this.completeFilter.start_date !== '' || this.completeFilter.end_date !== '') {
                this.completeFilter.page = this.serverParams.page;
                this.completeFilter.perPage = this.serverParams.perPage;
                this.filterCompletedDispatchesReports();
            } else {
                this.fetchCompletedDispatchesReports();
            }
            // this.fetchCompletedDispatchesReports();
        },

        onPerPageChangeCompleted(params) {
            this.updateParamsCompleted({perPage: params.currentPerPage});
            this.fetchCompletedDispatchesReports();
        },

        onSortChangeCompleted(params) {
            this.serverParams.sort = {
                type: params[0].type,
                field: params[0].field,
            };
            this.fetchCompletedDispatchesReports();
        },

        updateParamsPending(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChangePending(params) {
            console.log('change pending')
            this.updateParamsPending({page: params.currentPage});
            if (this.pendingFilter.start_date !== '' || this.pendingFilter.end_date !== '') {
                this.pendingFilter.page = this.serverParams.page;
                this.pendingFilter.perPage = this.serverParams.perPage;
                this.filterPendingDispatchesReports();
            } else {
                this.fetchPendingDispatchesReports();
            }

        },

        onPerPageChangePending(params) {
            this.updateParamsPending({perPage: params.currentPerPage});
            this.fetchPendingDispatchesReports();
        },

        onSortChangePending(params) {
            this.serverParams.sort = {
                type: params[0].type,
                field: params[0].field,
            };
            this.fetchPendingDispatchesReports();
        },

        async fetchData() {
            try {
                let {data: payload} = await axios.get(route("admin.reports.delivery.delivery-reports"));

                const delivery_counts = payload.delivery_counts;
                this.json_data = delivery_counts;
                const data = delivery_counts[0];

                const labels = Object.keys(data).map(key => key);
                console.log(labels);
                const values = Object.keys(data).map(key => data[key]);
                console.log(values);
                const colors = Object.keys(data).map(key => this.randomColor());

                this.stores = payload.stores;
                this.analyticsStores = payload.stores;
                this.riders = payload.riders;
                this.states = payload.states;
                this.products = payload.products || [];
                this.platforms = payload.platforms || {};
                this.orderTypes = payload.order_types || ['DELIVERY', 'PICKUP'];
                this.paymentStatuses = payload.payment_statuses || [];
                this.paymentChannels = payload.payment_channels || [];
                this.orderStatuses = payload.statuses || [];

                this.loaded = true;
                this.pieData = this.pieThumbprint(labels, values, colors);
            } catch (e) {
                alert(handleErrors(e.response));
            }
        },

        async fetchDeliveryAnalytics() {
            this.isLoadingAnalytics = true;
            try {
                const {data: {data}} = await axios.post(route('admin.reports.delivery.delivery-analytics'), this.analyticsFilter);
                this.analytics = {
                    number_of_deliveries: data.number_of_deliveries,
                    completed_deliveries: data.completed_deliveries,
                    pending_deliveries: data.pending_deliveries,
                    failed_deliveries: data.failed_deliveries,
                    average_delivery_time: data.average_delivery_time,
                    date_label: data.date_label || '',
                };
                this.storePerformanceBar = this.buildStorePerformanceBar(data.best_performing_stores || []);
            } catch (e) {
                alert(handleErrors(e.response));
            } finally {
                this.isLoadingAnalytics = false;
            }
        },

        buildStorePerformanceBar(stores) {
            if (!stores.length) {
                return null;
            }

            return {
                labels: stores.map(store => store.store_name),
                datasets: [{
                    label: 'Avg delivery time (minutes)',
                    backgroundColor: '#00B5B8',
                    data: stores.map(store => store.average_minutes),
                }],
            };
        },

        defaultAnalyticsFilter() {
            return {
                start_date: '',
                end_date: '',
                state_id: '',
                store_id: '',
                order_type: 'DELIVERY',
                ordered_from: '',
                payment_status: '',
                payment_channel: '',
                product_id: '',
                customer_type: '',
                status: '',
                rider_id: '',
            };
        },

        async resetDeliveryAnalytics() {
            this.analyticsFilter = this.defaultAnalyticsFilter();
            this.analyticsStores = this.stores;
            await this.fetchDeliveryAnalytics();
        },

        onAnalyticsStateChange() {
            if (this.analyticsFilter.state_id) {
                this.analyticsStores = this.stores.filter(
                    store => String(store.state_id) === String(this.analyticsFilter.state_id)
                );
            } else {
                this.analyticsStores = this.stores;
            }
            this.analyticsFilter.store_id = '';
        },

        async fetchPendingDispatchesReports() {
            try {
                const {
                    data: {
                        data,
                        total
                    }
                } = await axios.post(route("admin.reports.delivery.pending-dispatch-reports"), this.serverParams);
                this.pending_dispatches = data;
                this.totalRecords = total;
            } catch (e) {
                alert(handleErrors(e.response));
            }
        },

        async fetchCompletedDispatchesReports() {
            this.serverParams.unpaginated = false;
            try {
                const {
                    data: {
                        data,
                        total
                    }
                } = await axios.post(route("admin.reports.delivery.completed-dispatch-reports"), this.serverParams);

                this.completed_dispatches = data;
                console.log(this.completed_dispatches);
                this.totalRecords = total;
            } catch (e) {
                alert(handleErrors(e.response));
            }
        },

        async filterData() {
            try {
                this.isFiltered = true;
                let {data: {data}} = await axios.post(route("admin.reports.delivery.filter-delivery-reports"), this.reportFilter);

                this.json_data = data.deliveries;

                data = data.deliveries[0];

                const labels = Object.keys(data).map(key => key);
                const values = Object.keys(data).map(key => data[key]);
                const colors = Object.keys(data).map(key => this.randomColor());

                this.loaded = true;
                this.pieData = this.pieThumbprint(labels, values, colors);
            } catch (e) {
                alert(handleErrors(e.response))
            }
        },

        async filterCompletedDispatchesReports() {
            this.isLoadingCompletedFilter = true;
            this.completeFilter.rider_id = this.completedRiderId;
            try {
                const {
                    data: {
                        data,
                        total
                    }
                } = await axios.post(route("admin.reports.delivery.completed-dispatch-reports"), this.completeFilter);
                this.completed_dispatches = data;
                this.totalRecords = total;

                this.isLoadingCompletedFilter = false;
            } catch (e) {
                this.$store.commit('notifier/showNotifier', {message: e.response.data.message, type: 'danger'});
                this.isLoadingCompletedFilter = false;
            }
        },

        async filterPendingDispatchesReports() {
            this.isLoadingPendingFilter = true;
            this.pendingFilter.rider_id = this.pendingRiderId;
            try {
                const {
                    data: {
                        data,
                        total
                    }
                } = await axios.post(route("admin.reports.delivery.pending-dispatch-reports"), this.pendingFilter);
                this.pending_dispatches = data;
                this.totalRecords = total;

                this.isLoadingPendingFilter = false;
            } catch (e) {
                this.$store.commit('notifier/showNotifier', {message: e.response.data.message, type: 'danger'});
                this.isLoadingPendingFilter = false;
            }
        },

        async resetOverallFilter() {
            this.reportFilter = {};
            // this.completedRiderId = false;
            await this.fetchData();
        },

        async resetOverallFilterPending() {
            this.pendingFilter = {
                start_date: '',
                end_date: ''
            }
            this.pendingRiderId = null;
            this.fetchPendingDispatchesReports();
        },

        async resetOverallFilterComplete() {
            this.completeFilter = {
                start_date: '',
                end_date: ''
            }
            this.completedRiderId = null;
            this.fetchCompletedDispatchesReports();
        },

        async resetTotalDeliveryPickupFilter() {
            this.deliveryCount = {};
            this.storeId = '';
            this.stateId = '';
            await this.fetchPickupOrDeliveryCountReportByStore();
        },

        randomColor() {
            return '#' + Math.floor(Math.random() * 16777215).toString(16);
        },

        getCurrentMonthName() {
            const month = new Date().toLocaleString('en-us', {month: 'long'});
            const year = new Date().getFullYear();

            return `${month + ' ' + year}`;
        },

        dateFormatted(byFilter) {
            return formatDate(byFilter.start_date) + ' to ' + formatDate(byFilter.end_date)
        },

        generateReportName(name, fileType) {
            let now = new Date();
            let formatted = now.getFullYear() + '-' +
                String(now.getMonth() + 1).padStart(2, '0') + '-' +
                String(now.getDate()).padStart(2, '0') + '_' +
                String(now.getHours()).padStart(2, '0') + '-' +
                String(now.getMinutes()).padStart(2, '0') + '-' +
                String(now.getSeconds()).padStart(2, '0');

            return `${name}_${formatted}${fileType}`;
        },
    },

    computed: {
        exportPendingFields() {
            const fields = {};
            this.pending_columns.forEach(x => {
                fields[x.label] = x.field;
            });
            return fields;
        },

        exportCompletedFields() {
            const fields = {};
            this.completed_columns.forEach(x => {
                fields[x.label] = x.field;
            });
            return fields;
        },
    }
}
</script>
